import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { Button } from 'components/index';
import { DrawContext, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { useMagicReport } from '../context/MagicReportContext';
import { MagicReportSection } from './MagicReportSection';
import { MINOR_ADJUSTMENTS_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getField as getFieldUtil, getCommentsField } from './section-utils';
import { DeclarativeLayoutRenderer, DeclarativeLayoutConfig } from './DeclarativeLayoutRenderer';
import { NumberFieldRenderer } from './field-renderers/NumberFieldRenderer';
import {
  DrawAdjustment,
  MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT,
  MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT_MULTI,
  MINOR_ADJUSTMENTS_BOILERPLATE_TEXTS,
  MINOR_ADJUSTMENTS_WITHIN_THRESHOLD_TEXT,
  filterAdjustmentsAboveThreshold,
  formatSignedCurrency,
  resolveMinorAdjustmentsThreshold,
} from './minor-adjustments.util';

interface MinorAdjustmentsSectionProps {
  magicReportId: string;
}

/**
 * Minor Adjustments Section Content
 * Now using declarative config-driven rendering with specialized field types!
 */
const MinorAdjustmentsContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  cdccAdjustmentsValue: number;
  drawAdjustments: DrawAdjustment[];
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, cdccAdjustmentsValue, drawAdjustments, onBook }) => {
  const { reportState, registerSectionSync, unregisterSectionSync } = useMagicReport();
  const autoSync = Boolean(reportState.initialSyncPending);
  const thresholdValue = resolveMinorAdjustmentsThreshold(getField('Threshold')?.value);
  // Judged per payapp: any application over the threshold puts the section in the "greater than
  // normal" state and gets its own labelled table below. Shared with the export so both agree.
  const adjustmentsAboveThreshold = filterAdjustmentsAboveThreshold(drawAdjustments, thresholdValue);
  const isAboveThreshold =
    adjustmentsAboveThreshold.length > 0 || (drawAdjustments.length === 0 && Math.abs(cdccAdjustmentsValue) > thresholdValue);
  const valueColor = isAboveThreshold ? 'error.main' : 'success.main';
  const isMultiDraw = drawAdjustments.length > 1;

  const aboveThresholdText = isMultiDraw ? MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT_MULTI : MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT;

  const minorAdjustmentsTextValue = getField('MinorAdjustmentsText')?.value || '';
  const hasInitializedRef = useRef(false);

  // Auto-fill text field based on threshold, during the first open of a period only: on first mount
  // with no value, or while the field still holds one of the boilerplate variants.
  useEffect(() => {
    if (!autoSync) return;
    const isFirstMountWithNoValue = !hasInitializedRef.current && !minorAdjustmentsTextValue;
    const holdsOtherBoilerplate = MINOR_ADJUSTMENTS_BOILERPLATE_TEXTS.includes(minorAdjustmentsTextValue);
    hasInitializedRef.current = true;

    if (!isFirstMountWithNoValue && !holdsOtherBoilerplate) return;

    const newText = isAboveThreshold ? aboveThresholdText : MINOR_ADJUSTMENTS_WITHIN_THRESHOLD_TEXT;
    if (minorAdjustmentsTextValue !== newText) {
      handleFieldChange('MinorAdjustmentsText', { value: newText });
    }
  }, [isAboveThreshold, aboveThresholdText, minorAdjustmentsTextValue, handleFieldChange, autoSync]);

  // Function to restore boilerplate text
  const restoreBoilerplateText = () => {
    const boilerplateText = isAboveThreshold ? aboveThresholdText : MINOR_ADJUSTMENTS_WITHIN_THRESHOLD_TEXT;
    handleFieldChange('MinorAdjustmentsText', { value: boilerplateText });
  };

  // Register sync function to be called when "Run this Month" is clicked
  useEffect(() => {
    const syncFn = async () => {
      restoreBoilerplateText();
    };

    registerSectionSync('minorAdjustments', syncFn);
    return () => {
      unregisterSectionSync('minorAdjustments');
    };
  }, [registerSectionSync, unregisterSectionSync, isAboveThreshold, handleFieldChange]);

  const commentsField = getCommentsField(data, MINOR_ADJUSTMENTS_SECTION_CONFIG.config);

  // Build dynamic layout config with runtime functions
  const layout: DeclarativeLayoutConfig = {
    singleColumn: [
      // Text field with refresh button
      {
        fieldName: 'MinorAdjustmentsText',
        type: 'text-with-refresh',
        label: 'Minor Adjustments Text',
        onRefresh: restoreBoilerplateText,
      },
    ],
  };

  const expectedText = isAboveThreshold ? aboveThresholdText : MINOR_ADJUSTMENTS_WITHIN_THRESHOLD_TEXT;
  const isOutOfDate =
    !autoSync &&
    (!minorAdjustmentsTextValue || MINOR_ADJUSTMENTS_BOILERPLATE_TEXTS.includes(minorAdjustmentsTextValue)) &&
    minorAdjustmentsTextValue !== expectedText;

  return (
    <>
      {isOutOfDate && (
        <Alert severity='warning' sx={{ mb: 1, fontSize: '0.875rem' }}>
          Minor Adjustments are not synced with the current budget. Click &quot;Get Minor Adjustments&quot; to refresh the data.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant='outlined' size='small' onClick={restoreBoilerplateText}>
          Get Minor Adjustments
        </Button>
      </Box>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />

      {/* CDCC Adjustments and Threshold - Side by Side, aligned with text field */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 3 }}>
        {/* Empty space for bookmark icon alignment */}

        {/* Grid for the two fields */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, flexGrow: 1 }}>
          {/* CDCC Adjustments - Readonly display */}
          <NumberFieldRenderer
            value={`$${cdccAdjustmentsValue.toFixed(2)}`}
            addToActionItems={false}
            onChange={() => undefined}
            fieldName='CDCCAdjustments'
            label='Adjustments by CDCC'
            showBookmark={false}
            disabled
            valueColor={valueColor}
          />

          {/* Threshold - Editable number field */}
          <NumberFieldRenderer
            value={getField('Threshold')?.value ?? ''}
            addToActionItems={getField('Threshold')?.addToActionItems}
            onChange={(value, addToActionItems) => handleFieldChange('Threshold', { value, addToActionItems })}
            fieldName='Threshold'
            label='Threshold ($)'
            showBookmark={false}
          />
        </Box>

        {/* Empty space for delete icon alignment */}
        <Box sx={{ width: 26, flexShrink: 0 }} />
      </Box>

      {/*
        One labelled table per payapp whose adjustment exceeds the threshold. This is shown for a
        single payapp too — previously the amount only appeared in the readonly field above and the
        named table was missing entirely.
      */}
      {adjustmentsAboveThreshold.length > 0 && (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {adjustmentsAboveThreshold.map(adjustment => (
            <Box key={adjustment.drawId}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
                {adjustment.name} Minor Adjustments:
              </Typography>
              <Box
                component='table'
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& td': {
                    padding: '8px 12px',
                    borderBottom: '1px solid #e0e0e0',
                    fontSize: '0.875rem',
                  },
                  '& td:first-of-type': { fontWeight: 'medium' },
                  '& td:last-of-type': { textAlign: 'right' },
                }}
              >
                <tbody>
                  <tr>
                    <td>CDCC Adjustments:</td>
                    <td style={{ color: adjustment.amount < 0 ? '#d32f2f' : undefined }}>{formatSignedCurrency(adjustment.amount)}</td>
                  </tr>
                </tbody>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Comments Section */}
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange('Comments', updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </>
  );
};

/**
 * Minor Adjustments Section
 * Uses threshold-based logic to determine initial expanded state
 * Automatically collapses when adjustment value is below threshold
 */
export const MinorAdjustmentsSection: React.FC<MinorAdjustmentsSectionProps> = ({ magicReportId }) => {
  const { reportState } = useMagicReport();

  // Every covered payapp's adjustment. Reports opened before multi-draw have no draw list, so the
  // single primary-draw mirror becomes a one-payapp list.
  const drawAdjustments: DrawAdjustment[] = useMemo(() => {
    const draws = reportState.draws as DrawContext[] | undefined;
    if (draws?.length) {
      return draws.map(draw => ({
        drawId: draw.drawId,
        name: draw.drawSummary?.name || '[G702 Name]',
        amount: Number(draw.budgetSummary?.adjustmentsByCDCC) || 0,
      }));
    }
    if (!reportState.drawSummary) return [];
    return [
      {
        drawId: reportState.drawSummary.id,
        name: reportState.drawSummary.name || '[G702 Name]',
        amount: Number(reportState.budgetSummary?.adjustmentsByCDCC) || 0,
      },
    ];
  }, [reportState.draws, reportState.drawSummary, reportState.budgetSummary]);

  // The header chip shows every payapp's adjustments combined.
  const cdccAdjustmentsValue = drawAdjustments.length
    ? drawAdjustments.reduce((sum, d) => sum + d.amount, 0)
    : Number(reportState.budgetSummary?.adjustmentsByCDCC) || 0;

  /** Above threshold when ANY payapp's adjustment exceeds it. */
  const isAnyAboveThreshold = (threshold: number) =>
    drawAdjustments.length > 0 ? drawAdjustments.some(d => Math.abs(d.amount) > threshold) : Math.abs(cdccAdjustmentsValue) > threshold;

  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={MINOR_ADJUSTMENTS_SECTION_CONFIG}
      customization={{
        getInitialExpandedState: data => {
          const threshold = parseFloat(getFieldUtil(data, 'Threshold')?.value || '50');
          return isAnyAboveThreshold(threshold);
        },
        renderHeader: data => {
          const threshold = parseFloat(getFieldUtil(data, 'Threshold')?.value || '50');
          const valueColor = isAnyAboveThreshold(threshold) ? 'error.main' : 'success.main';

          return (
            <Typography variant='h5' sx={{ color: valueColor, fontWeight: 600 }}>
              ${Math.abs(cdccAdjustmentsValue).toFixed(2)}
            </Typography>
          );
        },
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <MinorAdjustmentsContent
            data={data}
            handleFieldChange={handleFieldChange}
            getField={getField}
            cdccAdjustmentsValue={cdccAdjustmentsValue}
            drawAdjustments={drawAdjustments}
            onBook={onBook}
          />
        ),
      }}
    />
  );
};
